import TaxTreatmentFields, { fiscalTaxLocked } from './TaxTreatmentFields';
// src/components/InvoiceForm.js
import React, { useState, useEffect } from "react";
/* global axios */
import api from '../services/apiService';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import debounce from "lodash.debounce";
import { isHoliday } from "./holidays"; // Pfad ggf. anpassen
import { Helmet } from 'react-helmet';  // Import von React Helmet
import { v4 as uuidv4 } from 'uuid';
import { formatDateDE, toDateInputValue } from '../utils/dateFormatter';

const defaultBuyerReference = customer => String(customer?.buyerReference ?? '').trim()
  || String(customer?.debtorNumber ?? '').trim();

// Funktion zur Formatierung von Datum+Uhrzeit in "TT.MM.JJJJ HH:MM"
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day     = String(date.getDate()).padStart(2, '0');
  const month   = String(date.getMonth() + 1).padStart(2, '0');
  const year    = date.getFullYear();
  const hours   = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const sortByInvoicePosition = (arr = []) =>
  [...arr].sort((a, b) =>
    (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) ||
    (a.designPosition ?? Number.MAX_SAFE_INTEGER) - (b.designPosition ?? Number.MAX_SAFE_INTEGER) ||
    (a.id ?? 0) - (b.id ?? 0)
  );


const InvoiceForm = ({ mode, onSave }) => {
  // invoiceId (bei Bearbeitung) und orderId (falls aus Auftrag übernommen)
  const { invoiceId, orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();



  // Mithilfe von URLSearchParams: Partial-Rechnung?
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type"); // z.B. "partial" oder "full"
  const isPartial = type === "partial";

  // 1) State für Invoice – zusätzlich das Feld billingCutoffDate für Teilrechnungen
  const [invoice, setInvoice] = useState({
    orderId: "", // Falls Rechnung aus Auftrag erzeugt wird
    customerId: "",
    buyerReference: "",
    contactId: "",
    lwOperatorId: "",
    date: "",
    validUntil: "",
    status: "pending",
    deliveryTime: "",
    operationLocation: "",
    notes: "",
    internalNotes: "",
    vatRate: 19,
    billingCutoffDate: "",
    totalNet: 0,
    vatAmount: 0,
    totalGross: 0,
  });

  

  // 2) State für InvoiceItems und verfügbare Artikel
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [editingRowId, setEditingRowId] = useState(null); // ID der Zeile, die bearbeitet wird


  // 3) State für Kunden, Kontakte und LW-Bediener
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [lwOperatorContacts, setLWOperatorContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedLWOperator, setSelectedLWOperator] = useState(null);
  const [lwPartnerId, setLwPartnerId] = useState(null);
  const [linkedOrder, setLinkedOrder] = useState(null); // { orderNumber, smartRental, ... }

  const [isLoading, setIsLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);


  

  // 4) Hilfsfunktionen (Debounce etc.)
  const filterCustomers = debounce((inputValue) => {
    if (inputValue.trim() === "") {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter((customer) =>
        `${customer.company} ${customer.firstName} ${customer.lastName}`
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, 300);

  const fetchFilteredItems = debounce(async (searchTerm) => {
    try {
      const response = await api.get(`/items?search=${searchTerm}`);
      // Auch hier nur aktive Artikel anzeigen
      const activeItems = response.data.filter(item => item.deactivated === false);
      setItems(activeItems);
    } catch (error) {
      console.error('Fehler beim Filtern der Artikel:', error);
    }
  }, 300);

// Sicheres Filter nur auf dem Label — verhindert .replace() auf non-Strings
const safeFilterOption = (option, inputValue) => {
  const label = (option?.label ?? '').toString();
  const q = (inputValue ?? '').toString();
  return label.toLowerCase().includes(q.toLowerCase());
};

const formatItemOptionLabel = (item) => {
  const description = String(item?.description || '').trim();
  return `${item.name}${description ? ` - ${description}` : ''} (${item.itemType})`;
};



// Speichert nur eine einzelne Zeile
const saveEditedRowOrder = async (rowId) => {
  try {
    // 1) Finde den Eintrag anhand seiner echten DB-ID
    const item = invoiceItems.find(i => i.id === rowId);
    if (!item) return;

    // Prüfen, ob es sich um einen Mietartikel handelt
    const isRental = item.item?.itemType === "rental";

    // Nur beim Miet-Typ muss rentalPeriodFrom gesetzt sein
    if (isRental && !item.rentalPeriodFrom) {
      return alert("Bitte das Datum ‚Von‘ ausfüllen, bevor Du speicherst.");
    }

    // 2) Baue Payload aus diesem einzelnen Eintrag
    const payload = {
      quantity:           item.quantity,
      unitId:             item.unitId,
      rentalPeriodFrom:   item.rentalPeriodFrom,
      rentalPeriodTo:     null,  
      rentalTimeFrom:     item.rentalTimeFrom,
      rentalTimeTo:       item.rentalTimeTo,
      includeSaturday:    item.includeSaturday,
      includeSunday:      item.includeSunday,
      includeHolidays:    item.includeHolidays,
      pricePerUnit:       item.pricePerUnit,
      manualPriceActive:  item.manualPriceActive,
      description: item.description,
    };

    // 3) PUT-Request mit der echten DB-id
    const { data: updated } = await api.put(
      `/invoices/${invoice.id}/invoiceitems/${item.id}`,
      payload
    );
  
    // 4) NEU: updatedBy auf null setzen
    await api.post(`/invoices/${invoice.id}/reset-updatedby`);

    // 4) State: Ersetze nur genau dieses eine Objekt durch die vollständige Server-Antwort
    setInvoiceItems(prev =>
  prev
    .map(i => (i.id === updated.id ? updated : i))
    .sort((a, b) =>
      (a.designPosition ?? a.position ?? 0) -
      (b.designPosition ?? b.position ?? 0) ||
      (a.position ?? Number.MAX_SAFE_INTEGER) -
      (b.position ?? Number.MAX_SAFE_INTEGER) ||
      (a.id ?? 0) - (b.id ?? 0)
    )
);

    // 5) Bearbeitungsmodus beenden
    setEditingRowId(null);

  } catch (error) {
    console.error(`Fehler beim Speichern der Position ${rowId}:`, error);
    alert(`Fehler: Die Änderung der Position ${rowId} konnte nicht gespeichert werden.`);
  }
};


  const normalizeDate = toDateInputValue;


  const allowedTypes = ['rental', 'sale', 'service', 'sparePart', 'transport']; 

  // 5) useEffects

  // Hier den useEffect einbauen, um den Gesamtwert (total) zu überwachen
  useEffect(() => {
    const totalSum = invoiceItems.reduce((sum, it) => sum + (it.total || 0), 0);
    console.log("Berechnete Gesamtsumme:", totalSum);
    // Optional: Falls du den Gesamtwert in einen extra State übernehmen möchtest, z.B.:
    // setInvoice(prev => ({ ...prev, totalNet: totalSum }));
  }, [invoiceItems]);

  // Kunden laden
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await api.get("/customers");
        setCustomers(res.data);
        setFilteredCustomers(res.data);
      } catch (err) {
        console.error("Fehler beim Laden der Kunden:", err);
      }
    };
    fetchCustomers();
  }, []);

  // Artikel laden
  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const res = await api.get("/items");
        // Nur Artikel übernehmen, die nicht deaktiviert sind
        const activeItems = res.data.filter(item => item.deactivated === false);
        setItems(activeItems);
      } catch (error) {
        console.error("Fehler beim Laden der Artikel:", error);
      }
    };
    fetchAllItems();
  }, []);
  

  useEffect(() => {
  const fetchAllItems = async () => {
    try {
      const res = await api.get("/items");
      const activeItems = res.data.filter(item => item.deactivated === false);
      setItems(activeItems);
    } catch (error) {
      console.error("Fehler beim Laden der Artikel:", error);
    }
  };
  fetchAllItems();
}, []);

// lwLicensePartnerId einmalig laden
useEffect(() => {
  (async () => {
    try {
      const res = await api.get('/companysetups'); // liefert Array
      const first = Array.isArray(res.data) && res.data.length ? res.data[0] : null;
      setLwPartnerId(first?.lwLicensePartnerId ? String(first.lwLicensePartnerId).trim() : null);
    } catch (e) {
      console.error('CompanySetup laden fehlgeschlagen:', e);
    }
  })();
}, []);

// verknüpften Auftrag laden (Create: orderId aus URL, Edit: invoice.orderId)
useEffect(() => {
  const id = orderId || invoice?.orderId;
  if (!id) return;
  (async () => {
    try {
      const res = await api.get(`/orders/${id}`);
      setLinkedOrder(res.data || null);
    } catch (e) {
      console.error('Order laden fehlgeschlagen:', e);
    }
  })();
}, [orderId, invoice?.orderId]);

// Falls im Bearbeitungsmodus: Bestehende Invoice laden
useEffect(() => {
  if (mode === "edit" && invoiceId && !initialDataLoaded) {
    const fetchInvoiceData = async () => {
      try {
        const invoiceRes = await api.get(`/invoices/${invoiceId}`);
        const fetchedInvoice = invoiceRes.data;

        // 🟡 NEU: Items direkt aus der Invoice laden (kein separater /invoiceitems-Call)
        const itemsFromServer = Array.isArray(fetchedInvoice.invoiceItems)
          ? fetchedInvoice.invoiceItems.map(it => ({
              ...it,
              // 🔁 ÄNDERUNG: designPosition fallback auf position (wie bei Order)
              designPosition:
                it.designPosition !== null && it.designPosition !== undefined
                  ? it.designPosition
                  : (it.position ?? 0),
            }))
          : [];

       /* ⬇️⬇️⬇️  HIER NEU EINFÜGEN  ⬇️⬇️⬇️ */

// Wenn alle designPositions effektiv 0 sind → einmalig 1..n setzen und speichern
let prepared = [...itemsFromServer];
const allZero =
  prepared.length > 0 && prepared.every(it => (it.designPosition ?? 0) === 0);

if (allZero) {
  prepared = prepared.map((it, idx) => ({
    ...it,
    designPosition: idx + 1,
  }));

  try {
    await api.put(
      `/invoices/${invoiceId}/invoiceitems/designPositions`,
      prepared.map(i => ({ id: i.id, designPosition: i.designPosition }))
    );
  } catch (e) {
    console.warn('Initiale designPositions konnten nicht gespeichert werden:', e);
  }
}

// sortieren für die Anzeige (designPosition → position → 0)
const sortedItems = prepared.sort(
  (a, b) =>
    (a.designPosition ?? a.position ?? 0) -
    (b.designPosition ?? b.position ?? 0)
);
/* ⬆️⬆️⬆️  BIS HIER NEU  ⬆️⬆️⬆️ */


        // 🔁 ÄNDERUNG: Invoice setzen (Datumsfelder normalisieren wie gehabt)
        setInvoice({
          ...fetchedInvoice,
          buyerReference: String(fetchedInvoice.buyerReference ?? '').trim() || defaultBuyerReference(fetchedInvoice.customer),
          date: normalizeDate(fetchedInvoice.date),
          validUntil: normalizeDate(fetchedInvoice.validUntil),
          billingCutoffDate: normalizeDate(fetchedInvoice.billingCutoffDate),
        });

        // 🔁 ÄNDERUNG: Customer/Contact/LW-Operator aus der geladenen Invoice übernehmen
        if (fetchedInvoice.customer) {
          setSelectedCustomer(fetchedInvoice.customer);
        }
        if (fetchedInvoice.contact) {
          setSelectedContact(fetchedInvoice.contact);
        }
        if (fetchedInvoice.lwOperator) {
          setSelectedLWOperator(fetchedInvoice.lwOperator);
        }

        // 🟡 NEU: vorbereitete & sortierte Items in den State
        setInvoiceItems(sortedItems);
      } catch (err) {
        console.error("Fehler beim Laden der Rechnung:", err);
      } finally {
        setInitialDataLoaded(true);
        setIsLoading(false);
      }
    };
    fetchInvoiceData();
  } else {
    setIsLoading(false);
  }
}, [mode, invoiceId, initialDataLoaded]);

  
  

  // Falls im Create-Modus und orderId vorhanden, übernehmen wir Daten aus dem Auftrag
  useEffect(() => {
    if (mode !== "edit" && orderId) {
      const loadOrderItems = async () => {
        try {
          const orderItemsRes = await api.get(`/orders/${orderId}/orderitems`);
          const orderItems = orderItemsRes.data;
          // Transformation der OrderItems in InvoiceItems (hier 1:1)
          const transformedItems = orderItems.map(oi => ({
            invoiceId: "", // wird später gesetzt, wenn die Rechnung erstellt ist
            itemId: oi.itemId,
            mainItemId: oi.mainItemId || null,
            quantity: oi.quantity,
            unitId: oi.unitId,
            pricePerUnit: oi.pricePerUnit,
            total: oi.pricePerUnit * oi.quantity,
            rentalPeriodFrom: oi.rentalPeriodFrom,
            rentalPeriodTo: oi.rentalPeriodTo,
            rentalTimeFrom: oi.rentalTimeFrom,
            rentalTimeTo: oi.rentalTimeTo,
            includeSaturday: oi.includeSaturday,
            includeSunday: oi.includeSunday,
            includeHolidays: oi.includeHolidays,
          }));
          setInvoiceItems(transformedItems);
        } catch (error) {
          console.error("Fehler beim Laden der OrderItems:", error);
        }
      };
      loadOrderItems();
    }
  }, [mode, orderId]);

  // Kontakte für Besteller und LW-Bediener laden, wenn customerId vorhanden ist
  useEffect(() => {
    const loadContacts = async (customerId) => {
      try {
        if (!customerId) return;
        // Besteller
        const bestellerRes = await api.get(`/contacts/customer/${customerId}?contactFunction=Besteller`);
        setCustomerContacts(bestellerRes.data);
        // LW-Bediener
        const lwRes = await api.get(`/contacts/customer/${customerId}?contactFunction=LW-Bediener`);
        setLWOperatorContacts(lwRes.data);
      } catch (error) {
        console.error("Fehler beim Laden der Kontakte:", error);
      }
    };

    if (invoice.customerId) {
      loadContacts(invoice.customerId);
    }
  }, [invoice.customerId]);

  // Handler für Kundenauswahl
  const handleCustomerSearchChange = (inputValue) => {
    filterCustomers(inputValue);
  };

  useEffect(() => {
    let canceled = false;
    if (invoice.customerId) {
      api.get(`/customers/${invoice.customerId}`)
        .then(response => {
          if (canceled) return;
          setSelectedCustomer(response.data);
          setInvoice(prev => ({
            ...prev,
            buyerReference: String(prev.buyerReference ?? '').trim() || defaultBuyerReference(response.data),
          }));
        })
        .catch(error => console.error("Fehler beim Laden des Kunden:", error));
    }
    return () => { canceled = true; };
  }, [invoice.customerId]);

  const handleCustomerChange = (selectedOption) => {
    if (!selectedOption) {
      setInvoice((prev) => ({ ...prev, customerId: "", buyerReference: "", contactId: "", lwOperatorId: "" }));
      setSelectedCustomer(null);
      setCustomerContacts([]);
      setSelectedContact(null);
      setLWOperatorContacts([]);
      setSelectedLWOperator(null);
      return;
    }
    const custId = selectedOption.value;
    setInvoice((prev) => ({ ...prev, customerId: custId,
      buyerReference: String(prev.customerId) === String(custId) ? prev.buyerReference : "",
      contactId: "", lwOperatorId: "" }));
    const found = customers.find((c) => c.id === custId);
    if (found) {
      setSelectedCustomer(found);
      // Kontakte für den ausgewählten Kunden laden
      api.get(`/contacts/customer/${custId}?contactFunction=Besteller`)
        .then(res => setCustomerContacts(res.data))
        .catch(err => console.error("Fehler beim Laden der Besteller:", err));
      api.get(`/contacts/customer/${custId}?contactFunction=LW-Bediener`)
        .then(res => setLWOperatorContacts(res.data))
        .catch(err => console.error("Fehler beim Laden der LW-Bediener:", err));
    }
  };

  // Handler für Besteller-Auswahl
  const handleContactChange = (e) => {
    const selId = e.target.value;
    setInvoice((prev) => ({ ...prev, contactId: selId }));
    const sel = customerContacts.find(c => c.id.toString() === selId.toString());
    if (sel) setSelectedContact(sel);
  };

  // Handler für LW-Bediener-Auswahl
  const handleLWOperatorChange = (e) => {
    const selId = e.target.value;
    setInvoice((prev) => ({ ...prev, lwOperatorId: selId }));
    const sel = lwOperatorContacts.find(c => c.id.toString() === selId.toString());
    if (sel) setSelectedLWOperator(sel);
  };

  // Handler für allgemeine Formularänderungen
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  // Handler zum Hinzufügen eines neuen InvoiceItems
  const addInvoiceItem = async () => {
    try {
      if (!selectedItemId || selectedQuantity <= 0) {
        alert("Bitte Artikel und gültige Menge auswählen.");
        console.warn("Ungültige Eingabedaten:", { selectedItemId, selectedQuantity });
        return;
      }
  
      const item = items.find((item) => item.id === selectedItemId);
      if (!item) {
        alert("Ausgewählter Artikel existiert nicht.");
        return;
      }
  
      // Bestimme die finale Einheit basierend auf itemType
      let finalUnitId = null;
  
      switch (item.itemType) {
        case "insurance":
          // Versicherungsartikel => standardmäßig 33 (Prozent)
          finalUnitId = 33;
          break;

        case "insuranceFixed":
          finalUnitId = 29;  // Tag
          break;
  
        case "rental":
          // Mietartikel => Fallback aus typePricings oder Transport
          finalUnitId =
            item.machineType?.typePricings?.[0]?.unit?.id ||
            item.transportPricings?.[0]?.unit?.id ||
            29; // oder 27 - je nachdem was du als Default willst (Stunde / Tag)
          break;
  
        case "transport":
          // Transportartikel => Falls du TransportPricing hast
          finalUnitId = item.transportPricings?.[0]?.unit?.id || 34; 
          // 34 wäre z.B. "pauschal" – passe das an dein System an
          break;
  
        case "sparePart":
        case "service":
        case "sale":
          // Ersatzteil / Service / Verkauf => aus salesPricings
          finalUnitId = item.salesPricings?.[0]?.unitId || null;
          break;
  
        default:
          // Falls es noch andere Typen gibt
          console.log(`Kein Fallback definiert für itemType='${item.itemType}'.`);
          finalUnitId = null;
          break;
      }
  
      // Falls finalUnitId immer noch null ist, wirf einen Fehler oder setze einen Default
      if (!finalUnitId) {
        console.warn(`Keine passende Einheit für itemType='${item.itemType}' gefunden; bitte manuell zuweisen.`);
         // finalUnitId = 29; // z.B. Tag als letzter Ausweg
      }
  
      // Payload vorbereiten
      const payload = {
        invoiceId: invoice.id, // ID der aktuellen Rechnung
        itemId: selectedItemId,
        quantity: selectedQuantity,
        unitId: finalUnitId, // Einheit setzen
        rentalPeriodFrom: null,    // <-- nun ok für nullable TIMESTAMP
      rentalPeriodTo:   null,    // <-- ditto
      rentalTimeFrom:   null,
      rentalTimeTo:     null,
        manualPriceActive: false,

      };
  
      console.log("Daten für den POST-Request:", payload);
  
      // 1) Haupt- und Linked-Items anlegen
    const { mainInvoiceItem, linkedInvoiceItems } = await api
      .post(`/invoices/${invoiceId}/invoiceitems`, payload)
      .then(res => res.data);

    // 2) Haupt-Item komplett mit Includes nachladen
    const fullMain = await api
      .get(`/invoices/${invoiceId}/invoiceitems/${mainInvoiceItem.id}`)
      .then(res => res.data);

    // 3) Verknüpfte Items komplett mit Includes nachladen
    const fullLinked = await Promise.all(
      linkedInvoiceItems.map(li =>
        api
          .get(`/invoices/${invoiceId}/invoiceitems/${li.id}`)
          .then(res => res.data)
      )
    );

    // nach fullMain und fullLinked geholt:
const mainWithTemp = {
  ...fullMain,
  tempId: uuidv4(),         // neu
  manualPriceActive: false,
};
const linkedWithTemp = fullLinked.map(item => ({
  ...item,
  tempId: uuidv4(),         // neu
  manualPriceActive: false,
}));

   // 4) Haupt- und Linked-Items ans Ende hängen und nach position sortieren
  // Haupt- und Linked-Items ans Ende hängen und nach position sortieren
const normalizedNew = [fullMain, ...fullLinked].map(i => ({
  ...i,
  designPosition: i.designPosition ?? i.position ?? 0,
}));

// ► NEU: End-Position bestimmen und vergeben
const currentMaxDesign = (invoiceItems.length
  ? Math.max(
      ...invoiceItems.map(x =>
        (x.designPosition ?? x.position ?? 0)
      )
    )
  : 0
);

let nextPos = currentMaxDesign + 1;
const normalizedNewWithEndPos = normalizedNew.map(i => ({
  ...i,
  designPosition: nextPos++,
}));

// ► NEU: (optional) direkt serverseitig speichern, damit Reihenfolge stabil bleibt
try {
  await api.put(
    `/invoices/${invoiceId}/invoiceitems/designPositions`,
    normalizedNewWithEndPos.map(i => ({ id: i.id, designPosition: i.designPosition }))
  );
} catch (e) {
  console.warn('Konnte designPositions nicht sofort speichern:', e);
}

setInvoiceItems(prev => {
  const merged = [...prev, ...normalizedNewWithEndPos];
  return merged.sort(
    (a, b) =>
      (a.designPosition ?? a.position ?? 0) -
      (b.designPosition ?? b.position ?? 0) ||
      (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) ||
      (a.id ?? 0) - (b.id ?? 0)
  );
});

    // 6) Eingaben zurücksetzen
    setSelectedItemId(null);
    setSelectedQuantity(1);

  } catch (error) {
    console.error('Fehler beim Hinzufügen der Angebotsposition:', error);
  }
};




  const getUnitId = (item) => {
    // Überprüfe, ob eine Einheit im TypePricing existiert
    const typePricingUnitId = item?.machineType?.typePricings?.[0]?.unitId;

    // Falls keine Einheit im TypePricing gefunden, überprüfe das TransportPricing
    const transportUnitId = item?.transportPricings?.[0]?.unitId;

    // Setze die Einheit für Versicherungsartikel auf "Prozent" (id: 33)
    if (item?.itemType === 'insurance') {
      return 33;
    }

    // Wenn keine Einheit gefunden wurde, gebe null zurück
    return typePricingUnitId || transportUnitId || null;
  };
  

  const saveInvoiceItemUnit = async (invoiceId, invoiceItem) => {
    try {
      if (!invoiceId) {
        throw new Error("InvoiceId ist nicht definiert");
      }
  
      if (!invoiceItem || typeof invoiceItem !== "object") {
        throw new Error("InvoiceItem ist nicht definiert oder kein gültiges Objekt");
      }
  
      if (!invoiceItem.id) {
        throw new Error("Fehlende id für InvoiceItem");
      }
  
      if (invoiceItem.unitId === undefined || invoiceItem.unitId === null) {
        throw new Error("Fehlende unitId für InvoiceItem");
      }
  
      // Behandlung für Versicherungsartikel
      if (invoiceItem.itemType === "insurance" && invoiceItem.unitId !== 33) {
        invoiceItem.unitId = 33; // Korrigiere auf "Prozent"
      }

      // Behandlung für Fixed-Versicherung (MBV fix)
    if (invoiceItem.itemType === "insuranceFixed") {
      invoiceItem.unitId = 29; // Tag hartkodiert
      // Menge und Zeitraum berechnet der Server dann automatisch neu
    }
  
      // Sende die Daten ans Backend
      const response = await api.put(
        `/invoices/${invoiceId}/invoiceitems/${invoiceItem.id}`,
        invoiceItem
      );
  
      console.log("Rechnungsposition erfolgreich gespeichert:", response.data);
      alert("Einheit wurde erfolgreich aktualisiert!");
    } catch (error) {
      console.error("Fehler beim Speichern der Rechnungsposition:", error.message || error);
      alert(`Fehler beim Speichern: ${error.message || "Unbekannter Fehler"}`);
    }
  };
  

  const saveChanges = async () => {
  try {
    console.log('Speichere Daten der Rechnungspositionen:', invoiceItems);

    // Nur manuell hinzugefügte InvoiceItems bearbeiten!
    const manuallyAddedItems = invoiceItems.filter(
      item => !item.orderItemId && !item.interimItemId
    );

    if (manuallyAddedItems.length === 0) {
      alert("Es gibt keine bearbeitbaren (manuell hinzugefügten) Positionen.");
      setEditingRowId(null);
      return;
    }

    const updatePromises = manuallyAddedItems.map(async (item) => {
      const payload = {
        id: item.id,
        itemId: item.itemId,
        quantity: item.quantity,
        unitId: item.unitId,
        rentalPeriodFrom: item.rentalPeriodFrom,
        rentalPeriodTo: item.rentalPeriodTo,
        rentalTimeFrom: item.rentalTimeFrom,
        rentalTimeTo: item.rentalTimeTo,
        includeSaturday: item.includeSaturday,
        includeSunday: item.includeSunday,
        includeHolidays: item.includeHolidays,
        pricePerUnit: item.pricePerUnit,
        description: item.description,
        manualPriceActive: item.manualPriceActive,
        ...(item.unitId !== undefined && { unitId: item.unitId }),
      };

      try {
        const response = await api.put(
          `/invoices/${invoice.id}/invoiceitems/${item.id}`,
          payload
        );
        if (response.status === 200 || response.status === 204) {
          console.log(`Position ${item.id} erfolgreich gespeichert.`);
        }
      } catch (err) {
        console.error(`Fehler beim Speichern der Position ${item.id}:`, err);
        throw new Error(`Fehler beim Speichern der Position ${item.id}`);
      }
    });

    await Promise.all(updatePromises);

    await api.post(`/invoices/${invoiceId}/reset-updatedby`);

    console.log('Alle manuell bearbeitbaren Positionen erfolgreich gespeichert.');

    // Abrufen der aktuellen Positionen
    const updatedResponse = await api.get(`/invoices/${invoice.id}/invoiceitems`);
    console.log('Backend-Daten vor Verarbeitung:', updatedResponse.data);

   if (Array.isArray(updatedResponse.data)) {
  // 1) Merge: lokale Flags (manualPriceActive) erhalten
  const mergedItems = updatedResponse.data.map(backendItem => {
    const localItem = invoiceItems.find(item => item.id === backendItem.id);
    return {
      ...backendItem,
      manualPriceActive:
        localItem && typeof localItem.manualPriceActive !== 'undefined'
          ? localItem.manualPriceActive
          : backendItem.manualPriceActive
    };
  });

  // 2) designPosition normalisieren (Fallback auf position → 0)
  let prepared = mergedItems.map(it => ({
    ...it,
    designPosition:
      it.designPosition !== null && it.designPosition !== undefined
        ? it.designPosition
        : (it.position ?? 0),
  }));

  // 3) Wenn alle (effektiv) 0 sind → einmalig 1..n vergeben und serverseitig speichern
  const allZero = prepared.length > 0 && prepared.every(it => (it.designPosition ?? 0) === 0);
  if (allZero) {
    prepared = prepared.map((it, idx) => ({ ...it, designPosition: idx + 1 }));
    try {
      await api.put(
        `/invoices/${invoice.id}/invoiceitems/designPositions`,
        prepared.map(i => ({ id: i.id, designPosition: i.designPosition }))
      );
    } catch (e) {
      console.warn('Initiale designPositions konnten nicht gespeichert werden:', e);
    }
  }

  // 4) Stabil sortieren für die Anzeige
  const sorted = [...prepared].sort((a, b) =>
    (a.designPosition ?? a.position ?? 0) - (b.designPosition ?? b.position ?? 0) ||
    (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) ||
    (a.id ?? 0) - (b.id ?? 0)
  );

  setInvoiceItems(sorted);
  console.log('InvoiceItems nach Backend-Synchronisation (mit merge & Ordnung):', sorted);
}

alert('Alle Änderungen wurden erfolgreich gespeichert!');
setEditingRowId(null);

  } catch (error) {
    console.error('Fehler beim Speichern der Änderungen:', error);
    alert('Fehler: Änderungen konnten nicht gespeichert werden.');
  }
};

// === NEU: Reihenfolge verschieben (analog Order) ===
const moveInvoiceItemUp = (id) => {
  setInvoiceItems(prev => {
    const sorted = [...prev].sort(
      (a, b) => (a.designPosition ?? a.position ?? 0) - (b.designPosition ?? b.position ?? 0)
    );
    const idx = sorted.findIndex(i => i.id === id);
    if (idx > 0) {
      const cur = sorted[idx].designPosition ?? sorted[idx].position ?? idx + 1;
      const prevPos = sorted[idx - 1].designPosition ?? sorted[idx - 1].position ?? idx;
      sorted[idx].designPosition = prevPos;
      sorted[idx - 1].designPosition = cur;
      saveInvoiceItemDesignPositions(sorted);
    }
    return [...sorted];
  });
};

const moveInvoiceItemDown = (id) => {
  setInvoiceItems(prev => {
    const sorted = [...prev].sort(
      (a, b) => (a.designPosition ?? a.position ?? 0) - (b.designPosition ?? b.position ?? 0)
    );
    const idx = sorted.findIndex(i => i.id === id);
    if (idx !== -1 && idx < sorted.length - 1) {
      const cur = sorted[idx].designPosition ?? sorted[idx].position ?? idx + 1;
      const nextPos = sorted[idx + 1].designPosition ?? sorted[idx + 1].position ?? idx + 2;
      sorted[idx].designPosition = nextPos;
      sorted[idx + 1].designPosition = cur;
      saveInvoiceItemDesignPositions(sorted);
    }
    return [...sorted];
  });
};

const saveInvoiceItemDesignPositions = async (items) => {
  try {
    await api.put(
      `/invoices/${invoice.id}/invoiceitems/designPositions`,
      items.map(i => ({ id: i.id, designPosition: i.designPosition }))
    );
  } catch (error) {
    console.error('Fehler beim Speichern der Reihenfolge:', error);
  }
};

  

  // Handler zum Entfernen eines InvoiceItems
  const removeInvoiceItem = async (invItemId) => {
    try {
      const res = await api.delete(`/invoices/${invoice.id}/invoiceitems/${invItemId}`);
      if (res.status === 200 || res.status === 204) {
        setInvoiceItems((prev) => prev.filter((item) => item.id !== invItemId));
      }
    } catch (err) {
      console.error("Fehler beim Löschen der Rechnungsposition:", err);
    }
  };

  // Handler zum Speichern der Rechnung
  const handleSaveInvoice = async () => {
    if (!invoice.customerId) {
      alert("Bitte wählen Sie einen Kunden aus.");
      return;
    }
    try {
      // Setze Rechnungs- und Gültigkeitsdatum
      const invoiceDate = invoice.date || new Date().toISOString();
      const validUntilDate =
        invoice.validUntil ||
        new Date(new Date(invoiceDate).setDate(new Date(invoiceDate).getDate() + 14)).toISOString();
  
      let totalNet, vatAmountCalculated, totalGrossCalculated, remainingNet;
  
      if (invoice.invoiceType === "downPayment") {
        // Bei Abschlagsrechnungen übernehmen wir die bereits gesetzten Werte
        totalNet = invoice.totalNetDownPayment || 0;
        vatAmountCalculated = invoice.vatAmount || 0;
        totalGrossCalculated = invoice.totalGross || 0;
        remainingNet = 0; // Für Abschlagsrechnungen setzen wir remainingNet auf 0
      } else {
        // Für finale Rechnungen: Berechne den aktuellen Netto-Betrag aus den InvoiceItems
        totalNet = invoiceItems.reduce((sum, it) => sum + (it.total || 0), 0);
        vatAmountCalculated = totalNet * (invoice.vatRate / 100);
        totalGrossCalculated = totalNet + vatAmountCalculated;
  
        // Ermittle die Summe aller bereits geleisteten Abschlagszahlungen (Down‑Payments)
        const totalDownPayments =
  invoice.order && invoice.order.invoices
    ? invoice.order.invoices
        .filter(inv => inv.invoiceType === "downPayment" && !inv.creditNoteCreated)
        .reduce((sum, inv) => sum + (parseFloat(inv.totalNetDownPayment) || 0), 0)
    : 0;

remainingNet = totalNet - totalDownPayments;
      }
  
      // Erstelle das finale Payload
      const finalPayload = {
        ...invoice,
        buyerReference: String(invoice.buyerReference ?? '').trim() || defaultBuyerReference(selectedCustomer),
        date: invoiceDate,
        validUntil: validUntilDate,
        totalNet: totalNet,
        vatAmount: vatAmountCalculated,
        totalGross: totalGrossCalculated,
        remainingNet: remainingNet,
      };
  
      if (fiscalTaxLocked(invoice)) {
        // Preserve persisted amounts when editing metadata or payments.
        for (const field of ['totalNet', 'vatAmount', 'totalGross', 'remainingNet', 'totalNetDownPayment']) {
          delete finalPayload[field];
        }
      }
      let response;
       // 1) Speichern und invoiceNumber ermitteln
      let invoiceNumber;
      if (mode === "edit") {
        response = await api.put(`/invoices/${invoiceId}`, finalPayload);
        alert("Rechnung erfolgreich aktualisiert!");
        // Rechnungsnummer aus dem State (invoice.invoiceNumber) nutzen
        invoiceNumber = invoice.invoiceNumber;
      } else {
        response = await api.post("/invoices", finalPayload);
        invoiceNumber = response.data.invoiceNumber;
        alert(`Rechnung erfolgreich erstellt! Rechnungsnummer: ${invoiceNumber}`);
      }

      // Optional: Falls benötigt, Kunden neu laden etc.
      if (onSave) onSave();
      // 2) Zur Liste mit Filter springen
      navigate(`/invoices?invoiceNumber=${encodeURIComponent(invoiceNumber)}`);
    } catch (err) {
      console.error("Fehler beim Speichern der Rechnung:", err);
      alert(err.response?.data?.error || "Fehler beim Speichern der Rechnung.");
    }
  };


  // Bedingungen & URL (keine Hooks, ESLint-safe)
const smartRentalActive = Boolean(
  (linkedOrder && linkedOrder.smartRental) ??
  (invoice?.order && invoice.order.smartRental)
);

const orderNumberSafe = (
  (linkedOrder && linkedOrder.orderNumber) ??
  (invoice?.order && invoice.order.orderNumber) ??
  ''
).toString().trim();

const reportUrl = smartRentalActive && lwPartnerId && orderNumberSafe
  ? `https://www.liftworker.de/app/auftragsauswertung_${encodeURIComponent(String(lwPartnerId).trim())}_${encodeURIComponent(orderNumberSafe)}.pdf`
  : null;

const openSmartRentalReport = () => {
  if (!reportUrl) return;
  window.open(reportUrl, '_blank', 'noopener,noreferrer');
};


  
  if (isLoading) return <div>Daten werden geladen ...</div>;
  


  // Kundenoptionen für Select
  const customerOptions = filteredCustomers.map((customer) => ({
    value: customer.id,
    label: `${customer.company} ( ${customer.customerNumber})`,
  }));

  return (
    <div key={invoiceId} className="invoice-form-container">
      <Helmet>
        <title>Rechnung bearbeiten</title>
        <meta name="description" content="Rechnung bearbeiten" />
      </Helmet>
      <h1>{mode === "edit" ? "Rechnung bearbeiten" : "Neue Rechnung erstellen"}</h1>

      {mode === "edit" && (
      <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
        <div>
          <strong>Erstellt von:</strong>{' '}
          {invoice.creator
            ? `${invoice.creator.firstName} ${invoice.creator.lastName}`
            : 'Unbekannt'}{' '}
          am {formatDateTime(invoice.createdAt)}
        </div>
        <div>
          <strong>Zuletzt geändert von:</strong>{' '}
          {invoice.updator
            ? `${invoice.updator.firstName} ${invoice.updator.lastName}`
            : 'Unbekannt'}{' '}
          am {formatDateTime(invoice.updatedAt)}
        </div>
      </div>
    )}
      
      <div className="form-row">
        {/* Linke Spalte: Kunde und Kontakte */}
        <div className="col">
  <label htmlFor="customerId" className="full-width">
    
    <Select
      id="customerId"
      name="customerId"
      options={customerOptions}
      onInputChange={handleCustomerSearchChange}
      onChange={handleCustomerChange}
      value={customerOptions.find((opt) => opt.value === invoice.customerId) || null}
      className="select-box-cst"
      isClearable
      placeholder="Kunde suchen..."
    />
  </label>
  {selectedCustomer && (
    <div className="customer-details">
      <p><strong>Firma:</strong> {selectedCustomer.company}</p>
      <p><strong>Zusatz:</strong> {selectedCustomer.companyAddon}</p>
      <p><strong>Anrede:</strong> {selectedCustomer.salutation} {selectedCustomer.firstName} {selectedCustomer.lastName}</p>
      <p><strong>Adresse:</strong> {selectedCustomer.street}, {selectedCustomer.postalCode} {selectedCustomer.city}</p>
      <TaxTreatmentFields document={invoice} onChange={setInvoice} customer={selectedCustomer} readOnly />
      {/* Anzeige der Zahlungskondition */}
      {selectedCustomer.paymentCondition && (
        <p>
          <strong>Zahlungskondition:</strong> {selectedCustomer.paymentCondition.name}
        </p>
      )}
    </div>

  
  )}

  {/* SmartRental-Auswertung – unter Zahlungskondition */}
{smartRentalActive && (
  <button
    type="button"
    onClick={openSmartRentalReport}
    disabled={!reportUrl}
    title={
      !orderNumberSafe
        ? 'Auftragsnummer fehlt – verknüpften Auftrag prüfen.'
        : (!lwPartnerId
          ? 'lwLicensePartnerId fehlt im CompanySetup.'
          : 'Auftragsauswertung öffnen')
    }
      style={{
      display: 'block',           // <— neu
      margin: '8px auto 0',       // <— neu: zentriert horizontal
      width: 100,                 // feste Breite hilft beim Zentrieren
      fontSize: 12,
      padding: '4px 8px',
      borderRadius: 6,
      border: '1px solid #ddd',
      cursor: reportUrl ? 'pointer' : 'not-allowed',
      background: reportUrl ? '#9c28a7ff' : '#eee',
      color: '#fff',              // für Kontrast
      lineHeight: 1.2
    }}
  >
    Auswertung
  </button>
)}

</div>

        {/* Mittlere Spalte: Rechnungsdaten */}
        <div className="col">

          {/* Anlieferzeit Feld */}
          <label htmlFor="deliveryTime" className="full-width">
              Anlieferzeit:
              <input
                type="time"
                id="deliveryTime"
                name="deliveryTime"
                value={invoice.deliveryTime || ""}
                onChange={handleChange}
                className="input-box"
              />
            </label>

            {/* Einsatzort (operationLocation) */}
            <label htmlFor="operationLocation" className="full-width">
              Einsatzort:
              <input
                type="text"
                id="operationLocation"
                name="operationLocation"
                value={invoice.operationLocation || ""}
                onChange={handleChange}
                className="input-box"
                placeholder="Strasse, PLZ, Ort"
              />
            </label>

           {/* Ansprechpartner vor Ort */}
           <label htmlFor="lwOperatorId" className="full-width">
              Ansprechpartner vor Ort:
              <select
                id="lwOperatorId"
                name="lwOperatorId"
                value={invoice.lwOperatorId}
                onChange={handleLWOperatorChange}
                className="select-box"
                style={{ marginLeft: '0px' }}
              >
                <option value="">Bitte Ansprechpartner vor Ort wählen</option>
                {lwOperatorContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.contactFirstName} {contact.contactLastName}
                  </option>
                ))}
              </select>
            </label>
          
            <label htmlFor="notes" className="full-width" style={{ marginTop: '5px' }}>
           
            <textarea
              id="notes"
              name="notes"
              value={invoice.notes || ""}
              onChange={handleChange}
              rows="4"
              style={{ width: '98%', marginBottom: '20px', marginTop: '20px' }} // Breite kann hier angepasst werden
              placeholder="Rechungsnotizen"
            />
          </label>

          <label htmlFor="internalNotes" className="full-width" style={{ marginTop: '5px' }}>
           
            <textarea
              id="internalNotes"
              name="internalNotes"
              value={invoice.internalNotes || ""}
              onChange={handleChange}
              rows="4"
              style={{ width: '98%', marginBottom: '20px', marginTop: '0px' }} // Breite kann hier angepasst werden
              placeholder="interne Rechungsnotizen"
            />
          </label>

          {/* Bei Teilrechnung: Rechnungsstichtag */}
          {mode !== "edit" && (
            <label htmlFor="billingCutoffDate">
              Rechnungsstichtag (bei Teilrechnung):
              <input
                type="date"
                id="billingCutoffDate"
                name="billingCutoffDate"
                value={invoice.billingCutoffDate || ""}
                onChange={handleChange}
              />
            </label>
          )}
        </div>

       {/* Rechte Spalte: Anzeige von Rechnungsnummer, Status und Besteller */}
<div className="col">
  
  {/* Button zum zugehörigen Auftrag */}
  <div style={{ marginBottom: '20px' }}>
    <button
      type="button"
      onClick={() => navigate(`/orders/${invoice.orderId}/edit`)} // Navigiere zur Auftragsseite mit der orderId
      style={{
        backgroundColor: '#007bff',
        color: '#fff',
        padding: '10px 20px',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '4px',
        marginBottom: '10px',
      }}
    >
      Zum Auftrag wechseln
    </button>
  </div>
Rechnungsdatum
          <label htmlFor="date"className="full-width">
          
            <input
              type="date"
              id="date"
              name="date"
              value={invoice.date || ""  }
              className="select-box-bst"
              onChange={handleChange}
              
            />
          </label>
          {invoice.invoiceNumber && (
  <p>
    <strong>Rechnungsnummer:</strong> {invoice.invoiceNumber}
  </p>
)}
{invoice.referenceNumber && (
  <p>
    <strong>Bestellnummer:</strong> {invoice.referenceNumber}
  </p>
)}
{invoice.order?.orderNumber && (
  <p><strong>Auftragsnummer:</strong> {invoice.order.orderNumber}</p>
)}
  <p>
    <strong>Status:</strong> {invoice.status}
  </p>
<label htmlFor="buyerReference" className="full-width">
  Leitweg-ID
  <input
    type="text"
    id="buyerReference"
    name="buyerReference"
    value={invoice.buyerReference || ""}
    onChange={handleChange}
    maxLength={255}
    className="select-box-bst"
    style={{ display: 'block', width: '60%', maxWidth: '228px', marginLeft: 'auto', marginRight: 'auto' }}
    aria-describedby="buyerReferenceHelp"
  />
</label>
<small id="buyerReferenceHelp">Ohne Leitweg-ID wird die Debitorennummer verwendet.</small>
{defaultBuyerReference(selectedCustomer) && defaultBuyerReference(selectedCustomer) !== invoice.buyerReference && (
  <button
    type="button"
    style={{ alignSelf: 'center', width: 'fit-content', maxWidth: '100%' }}
    onClick={() => setInvoice(prev => ({ ...prev, buyerReference: defaultBuyerReference(selectedCustomer) }))}
  >
    Aus Kundenstamm übernehmen
  </button>
)}
  
   {/* Besteller Auswahl und Anzeige */}
   {customerContacts.length > 0 && (
    <label htmlFor="contactId" className="full-width">
     
      <select
        id="contactId"
        name="contactId"
        value={invoice.contactId || ""}
        onChange={handleContactChange}
        className="select-box-bst"
      >
        <option value="">Besteller wählen</option>
        {customerContacts.map((contact) => (
          <option key={contact.id} value={contact.id}>
            {contact.contactFirstName} {contact.contactLastName}
          </option>
        ))}
      </select>
    </label>
  )}
  {selectedContact && (
    <div className="contact-details">
      <p>
        <strong>Besteller:</strong> {selectedContact.contactFirstName}{" "}
        {selectedContact.contactLastName}
      </p>
      <p>
        <strong>Email:</strong> {selectedContact.contactEmail}
      </p>
      <p>
        <strong>Telefon:</strong> {selectedContact.contactPhone}
      </p>
    </div>

  )}
</div>
        
      </div>
      
 <div className="col"></div>
      {/* Bereich InvoiceItems */}
     <div className="table-breakout" style={{ marginTop: '40px' }}>
  <h3>Rechnungspositionen</h3>
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Position</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Artikel</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Beschreibung</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Einheit</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Menge</th>
        <th style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>E-Preis</th>
        <th style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>Gesamt</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Von</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Bis</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Zeit Von</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Zeit Bis</th>
        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Aktionen</th>
      </tr>
    </thead>
    <tbody>
  {[...invoiceItems]
    .sort((a, b) => (a.designPosition ?? a.position ?? 0) - (b.designPosition ?? b.position ?? 0))
    .map((position, idx) => {

    const isEditing = editingRowId === position.id; // Bearbeitungsmodus
    const editingThisRow = editingRowId === position.id;
    const isManuallyAdded = !position.orderItemId && !position.interimItemId; // Manuell hinzugefügt, wenn keine orderItemId
    const isRental = position.item?.itemType === 'rental';
                  const isInsuranceItem = position.item?.itemType === 'insurance';
                  const unitName =
                    position.item?.machineType?.typePricings?.[0]?.unit?.name ||
                    position.item?.transportPricings?.[0]?.unit?.name ||
                    (isInsuranceItem ? 'Prozent' : 'n.a.');

                  const pricePerUnit = Number(position.pricePerUnit) || 0;
                  const total = Number(position.total) || 0;

                  const isLinkedItem = !!position.mainItemId;

                  // Formatierung des Einzelpreises
                  const formattedPricePerUnit = (typeof pricePerUnit === 'number' && !isNaN(pricePerUnit))
                    ? pricePerUnit.toFixed(2).replace('.', ',') + ' €'
                    : '0,00 €';

                  // Formatierung des Gesamtpreises
                  const formattedTotal = (typeof total === 'number' && !isNaN(total))
                    ? total.toFixed(2).replace('.', ',') + ' €'
                    : '0,00 €';

                    return (
                      <tr key={position.id ?? position.tempId}>
                        <td style={{ border: "1px solid #ddd", padding: "8px" }}>
  {idx + 1}
</td>
                        {/* Artikelname */}
                      <td style={{ border: '1px solid #ddd', padding: '8px', width: '25ch', minWidth: '25ch', maxWidth: '25ch' }}>
                      {editingThisRow && !isInsuranceItem ? (
                          <input
                            type="text"
                            value={position.item?.name || ''}
                            style={{ width: '25ch', minWidth: '25ch', maxWidth: '25ch' }}
                            onChange={(e) =>
                              setInvoiceItems((prev) =>
                                prev.map((item) =>
                                  item.id === position.id
                                    ? { ...item, item: { ...item.item, name: e.target.value } }
                                    : item
                                )
                              )
                            }
                          />
                        ) : (
                          <span
                            style={{
                              display: 'inline-block',
                              width: '25ch',
                              minWidth: '25ch',
                              maxWidth: '25ch',
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-all',
                            }}
                          >
                            {position.item?.name}
                          </span>
                        )}
                      </td>
                        {/* Beschreibung */}
<td style={{ border: '1px solid #ddd', padding: '8px' }}>
  {editingThisRow && !isInsuranceItem ? (
    <textarea
      value={position.description ?? position.item?.description ?? ''}
      wrap="soft"
      style={{ maxWidth: '320px' }}
      onChange={(e) =>
        setInvoiceItems((prev) =>
          prev.map((item) =>
            item.id === position.id
              ? { ...item, description: e.target.value }
              : item
          )
        )
      }
    />
  ) : (
    position.description || position.item?.description || 'Keine Beschreibung verfügbar'
  )}
</td>
                        {/* Einheit */}
<td style={{ border: '1px solid #ddd', padding: '8px' }}>
  {(editingThisRow && !(isInsuranceItem || position.item?.itemType === 'insuranceFixed')) ? (
    <>
      {/* Dropdown wie bisher */}
      <select
        value={position.unitId != null ? position.unitId.toString() : ''}
        onChange={(e) => {
          const newUnitId = e.target.value ? parseInt(e.target.value, 10) : null;
          setInvoiceItems((prev) =>
            prev.map((item) =>
              item.id === position.id
                ? { ...item, unitId: newUnitId }
                : item
            )
          );
          const updatedItem = { ...position, unitId: newUnitId };
          saveInvoiceItemUnit(invoice.id, updatedItem);
        }}
      >
        <option value="">Einheit wählen</option>
        {position.item?.machineType?.typePricings
          ?.map((pricing) => pricing.unit)
          ?.filter((unit, index, self) => unit && self.findIndex((u) => u.id === unit.id) === index)
          .map((unit) => (
            <option key={unit.id} value={unit.id.toString()}>
              {unit.name}
            </option>
          ))}
      </select>
    </>
  ) : (
    // Spezielle Anzeige für Versicherungsartikel:
    position.item?.itemType === 'insurance'
      ? 'Prozent'
      : position.item?.itemType === 'insuranceFixed'
        ? 'Tag'
        : (position.unit?.name || '')
  )}
</td>


                        {/* Menge */}
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
  {(editingThisRow && !(isInsuranceItem || position.item?.itemType === 'insuranceFixed')) ? (
    <input
      type="number"
      value={position.quantity || ''}
      onChange={(e) =>
        setInvoiceItems((prev) =>
          prev.map((item) =>
            item.id === position.id
              ? { ...item, quantity: parseFloat(e.target.value) || 0,
                manualPriceActive: false
                }
              : item
          )
        )
      }
      min="0"
    />
  ) : (
    position.item?.itemType === 'insurance'
      ? `${position.quantity}%`
      : position.item?.itemType === 'insuranceFixed'
        ? position.quantity // Zeige als Zahl (z. B. Anzahl Tage)
        : position.quantity
  )}
</td>

                        {/* Einzelpreis */}
<td style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>
{(editingThisRow && !(isInsuranceItem || position.item?.itemType === 'insuranceFixed')) ? (
    <input
      type="number"
      value={position.pricePerUnit || ''}
      onChange={(e) =>
        setInvoiceItems((prev) =>
          prev.map((item) =>
            item.id === position.id
              ? { 
                  ...item, 
                  pricePerUnit: parseFloat(e.target.value) || 0,
                  manualPriceActive: true  // Flag setzen, wenn der Preis geändert wurde
                }
              : item
          )
        )
      }
      min="0"
      step="0.01"
    />
  ) : (
    // Formatierung des Einzelpreises mit Komma und Euro-Symbol
    (typeof pricePerUnit === 'number' && !isNaN(pricePerUnit))
      ? pricePerUnit.toFixed(2).replace('.', ',') + ' €'
      : '0,00 €'
  )}
</td>
                        <td style={{ border: "1px solid #ddd", padding: "8px", whiteSpace: 'nowrap' }}>
                          {`${total.toFixed(2).replace(".", ",")} €`}
                        </td>
                        {/* Mietzeitraum Von */}
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {editingThisRow && !isInsuranceItem ? (
                              <input
                                type="date"
                                value={position.rentalPeriodFrom || ''}
                                onChange={(e) =>
                                  setInvoiceItems((prev) =>
                                    prev.map((item) =>
                                      item.id === position.id
                                        ? { ...item, rentalPeriodFrom: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            ) : (
                              position.rentalPeriodFrom ? formatDateDE(position.rentalPeriodFrom) : '-'
                            )}
                          </td>
                        {/* Mietzeitraum Bis */}
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {editingThisRow && !isInsuranceItem ? (
                              <input
                                type="date"
                                value={position.rentalPeriodTo || ''}
                                onChange={(e) =>
                                  setInvoiceItems((prev) =>
                                    prev.map((item) =>
                                      item.id === position.id
                                        ? { ...item, rentalPeriodTo: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            ) : (
                              // erst prüfen, ob wir ein echtes Datum haben (timestamp > 0)
    position.rentalPeriodTo && new Date(position.rentalPeriodTo).getTime() > 0
      ? formatDateDE(position.rentalPeriodTo)
      : ''
  )}
</td>
                        {/* Mietzeit Von */}
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {editingThisRow && !isInsuranceItem ? (
                              <input
                                type="time"
                                value={position.rentalTimeFrom || ''}
                                onChange={(e) => {
                                  console.log('Änderung in rentalTimeFrom:', e.target.value); // Debugging
                                  setInvoiceItems((prev) =>
                                    prev.map((item) =>
                                      item.id === position.id
                                        ? { ...item, rentalTimeFrom: e.target.value }
                                        : item
                                    )
                                  );
                                }}
                              />
                            ) : (
                              position.rentalTimeFrom || '-'
                            )}
                          </td>
                        {/* Mietzeit Bis */}
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {editingThisRow && !isInsuranceItem ? (
                              <input
                                type="time"
                                value={position.rentalTimeTo || ''}
                                onChange={(e) =>
                                  setInvoiceItems((prev) =>
                                    prev.map((item) =>
                                      item.id === position.id
                                        ? { ...item, rentalTimeTo: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            ) : (
                              position.rentalTimeTo || '-'
                            )}
                          </td>
                       <td style={{ border: "1px solid #ddd", padding: "8px", whiteSpace: 'nowrap' }}>
  {/* ▲▼ — NEU: analog OrderForm */}
  <button
    type="button"
    title="Nach oben"
    onClick={() => moveInvoiceItemUp(position.id)}
    style={{ marginRight: 6, padding: '2px 6px', color: '#fff', background: '#aaaaaa', border: 'none', borderRadius: 4 }}
  >▲</button>
  <button
    type="button"
    title="Nach unten"
    onClick={() => moveInvoiceItemDown(position.id)}
    style={{ marginRight: 10, padding: '2px 6px', color: '#fff', background: '#aaaaaa', border: 'none', borderRadius: 4 }}
  >▼</button>

  {/* — deine bisherigen Buttons unverändert — */}
  {isManuallyAdded ? (
    isEditing ? (
      <>
        <button onClick={() => saveChanges(position.id)} style={{ backgroundColor: "#28a745", color: "#fff", border: "none", padding: "5px 10px", marginRight: "5px", cursor: "pointer" }}>
          Speichern
        </button>
        <button onClick={() => setEditingRowId(null)} style={{ backgroundColor: "#6c757d", color: "#fff", border: "none", padding: "5px 10px", cursor: "pointer" }}>
          Abbrechen
        </button>
      </>
    ) : (
      <>
        {!(isInsuranceItem || position.item?.itemType === 'insuranceFixed') && (
          <button disabled={fiscalTaxLocked(invoice)} onClick={() => setEditingRowId(position.id)} style={{ backgroundColor: "#007bff", color: "#fff", border: "none", padding: "5px 10px", cursor: "pointer", marginRight: "5px" }}>
            Bearbeiten
          </button>
        )}
        <button disabled={fiscalTaxLocked(invoice)} onClick={() => removeInvoiceItem(position.id)} style={{ backgroundColor: "red", color: "#fff", border: "none", padding: "5px 10px", cursor: "pointer" }}>
          Löschen
        </button>
      </>
    )
  ) : (
    <button disabled={fiscalTaxLocked(invoice)} onClick={() => removeInvoiceItem(position.id)} style={{ backgroundColor: "red", color: "#fff", border: "none", padding: "5px 10px", cursor: "pointer" }}>
      Löschen
    </button>
  )}
</td>

                      </tr>
                    );
                  })}
                </tbody>

  </table>

  {/* Formular zum Hinzufügen neuer InvoiceItems */}
  <div className="order-item-addition" style={{ marginTop: '20px', width: '30%', marginLeft: '35%', }}>
    <h4>Neue Rechnungsposition hinzufügen</h4>
    <div className="form-group" style={{ marginBottom: '15px' , width: '50%',}}></div>
    <label htmlFor="itemId"></label>

    <label>Menge:</label>
    <input
      type="number"
      value={selectedQuantity}
      min="1"
      onChange={(e) => setSelectedQuantity(+e.target.value)}
      style={{ display: 'block', marginTop: '5px', padding: '8px', width: '100%' }}
    />

    <label></label>
    <Select
  id="itemId"
  options={
    // erst nur aktive Items, dann Whitelist anwenden
    items
      .filter(item => !item.deactivated && allowedTypes.includes(item.itemType))
      .map(item => ({
        value: item.id,
        label: formatItemOptionLabel(item)
      }))
  }
  value={
    // und Value entsprechend aus der gleichen, gefilterten Liste holen
    items
      .filter(item => !item.deactivated && allowedTypes.includes(item.itemType))
      .map(item => ({
        value: item.id,
        label: formatItemOptionLabel(item)
      }))
      .find(option => option.value === selectedItemId) || null
  }
  onChange={option => setSelectedItemId(option?.value || null)}
  onInputChange={(inputValue, { action }) => {
    if (action === 'input-change') {
      // nichts zurückgeben!
      void fetchFilteredItems(inputValue);
    }
  }} // Filterfunktion
  placeholder="Artikel suchen..."
  isClearable
  filterOption={safeFilterOption} 
  styles={{
    control: base => ({
      ...base,
      marginTop: '5px',
      width: '100%',
      marginLeft: '0',
      padding: '8px',
      marginBottom: '15px',
    })
  }}
/>
    
    <button disabled={fiscalTaxLocked(invoice)} onClick={addInvoiceItem}>Hinzufügen</button>
  </div>
</div>


      {/* Zusammenfassung */}
<div style={{ marginTop: "20px" }}>
  <h3>Zusammenfassung</h3>
  {(() => {
    // Falls es sich um eine Abschlagsrechnung handelt, verwenden wir die Header-Werte
    if (invoice.invoiceType === "downPayment") {
      const net = Number(invoice.totalNetDownPayment ?? invoice.totalNet ?? 0);
      const vat = fiscalTaxLocked(invoice) ? Number(invoice.vatAmount || 0) : net * (invoice.vatRate / 100);
      const gross = fiscalTaxLocked(invoice) ? Number(invoice.totalGross || 0) : net + vat;
      return (
        <>
          <p>
            <strong>Netto:</strong> {net.toFixed(2).replace(".", ",")} €
          </p>
          <p>
            <strong>MwSt. ({invoice.vatRate}%):</strong> {vat.toFixed(2).replace(".", ",")} €
          </p>
          <p>
            <strong>Brutto:</strong> {gross.toFixed(2).replace(".", ",")} €
          </p>
        </>
      );
    } else {
      // Für finale Rechnungen: Summiere die InvoiceItems
      const net = fiscalTaxLocked(invoice) ? Number(invoice.totalNet || 0) : invoiceItems.reduce((sum, it) => sum + Number(it.total || 0), 0);
      const vat = fiscalTaxLocked(invoice) ? Number(invoice.vatAmount || 0) : net * (invoice.vatRate / 100);
      const gross = fiscalTaxLocked(invoice) ? Number(invoice.totalGross || 0) : net + vat;
      return (
        <>
          <p>
            <strong>Netto:</strong> {net.toFixed(2).replace(".", ",")} €
          </p>
          <p>
            <strong>MwSt. ({invoice.vatRate}%):</strong> {vat.toFixed(2).replace(".", ",")} €
          </p>
          <p>
            <strong>Brutto:</strong> {gross.toFixed(2).replace(".", ",")} €
          </p>
        </>
      );
    }
  })()}
</div>

{/* Übersicht der bereits geleisteten Abschlagsrechnungen */}
{ invoice.invoiceType === "finalInvoice" &&
  invoice.order &&
  invoice.order.invoices &&
  invoice.order.invoices.filter(inv => inv.invoiceType === "downPayment" && !inv.creditNoteCreated).length > 0 && (
    <div style={{ marginTop: "20px" }}>
      <h3>Bisher geleistete Abschlagsrechnungen</h3>
      {invoice.order.invoices.filter(inv => inv.invoiceType === "downPayment").length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Rechnungsnummer</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Datum</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Netto</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>MwSt.</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Brutto</th>
            </tr>
          </thead>
          <tbody>
            {invoice.order.invoices
              .filter(inv => inv.invoiceType === "downPayment" && !inv.creditNoteCreated)
              .map(inv => (
                <tr key={inv.id}>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>{inv.invoiceNumber}</td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>{formatDateDE(inv.date)}</td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {parseFloat(inv.totalNetDownPayment).toFixed(2).replace(".", ",")} €
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {parseFloat(inv.vatAmount).toFixed(2).replace(".", ",")} €
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {parseFloat(inv.totalGross).toFixed(2).replace(".", ",")} €
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      ) : (
        <p>Keine Abschlagsrechnungen vorhanden.</p>
      )}

      <div style={{ marginTop: "20px" }}>
        <h3>Noch zu leistende Zahlung</h3>
        {(() => {
          // Berechne die Summe aller Abschlagsbeträge
          const totalDownPayments = invoice.order.invoices
            .filter(inv => inv.invoiceType === "downPayment" && !inv.creditNoteCreated)
            .reduce((sum, inv) => sum + (parseFloat(inv.totalNetDownPayment) || 0), 0);
          // Offener Betrag = Auftragssaldo minus bereits abgeschlagene Beträge
          const openAmount = invoice.totalNet - totalDownPayments;
          // Berechne VAT und Brutto auf Basis des offenen Betrags
          const vatOpen = openAmount * (invoice.vatRate / 100);
          const grossOpen = openAmount + vatOpen;
          return (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>Netto</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>MwSt.</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>Brutto</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {openAmount.toFixed(2).replace(".", ",")} €
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {vatOpen.toFixed(2).replace(".", ",")} €
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {grossOpen.toFixed(2).replace(".", ",")} €
                  </td>
                </tr>
              </tbody>
            </table>
          );
        })()}
      </div>
    </div>
)}



      {/* Action-Buttons */}
      <div style={{ marginTop: "20px" }}>
  {/*      <button
          type="button"
          onClick={() => navigate("/invoices")}
          style={{ marginRight: "10px", padding: "10px 20px", cursor: "pointer" }}
        >
          Zurück
        </button>    */}
        <button
          type="button"
          onClick={handleSaveInvoice}
          style={{ padding: "10px 20px", cursor: "pointer" }}
        >
          {mode === "edit" ? "Speichern" : "Erstellen"}
        </button>
      </div>
    </div>
  );
};

export default InvoiceForm;
